import { useState, type ReactElement, type SyntheticEvent } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import type { TenantViewModel } from '../api';

/**
 * Destructive typed-name confirmation for wiping a tenant (PTV2-1021 Phase 2).
 *
 * Wiping DROPS the tenant schema — irreversible. The confirm button stays
 * disabled until the operator types the EXACT tenant name (trimmed, but
 * case-sensitive — a fuzzy/case-insensitive match would defeat the safeguard).
 * The dialog copy must say, unmistakably, that this destroys a schema.
 */
export function WipeTenantConfirmDialog({
  tenant,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly tenant: TenantViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (tenant: TenantViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement | null {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');

  if (tenant === null) return null;

  const matches = typed.trim() === tenant.name;

  function handleClose(next: boolean): void {
    if (!next) {
      setTyped('');
      onCancel();
    }
  }

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    if (!matches || tenant === null) return;
    onConfirm(tenant);
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            {t('routes.platform.detail.wipe.title')}
          </DialogTitle>
          <DialogDescription>
            {t('routes.platform.detail.wipe.warning', { schema: tenant.schemaName })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-3">
          <label htmlFor="wipe-confirm-input" className="block text-sm">
            {t('routes.platform.detail.wipe.confirmLabel', { name: tenant.name })}
          </label>
          <Input
            id="wipe-confirm-input"
            value={typed}
            onChange={(e) => { setTyped(e.target.value); }}
            placeholder={t('routes.platform.detail.wipe.confirmPlaceholder')}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
          />
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.platform.detail.wipe.error', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { handleClose(false); }} disabled={pending}>
              {t('routes.platform.detail.wipe.cancel')}
            </Button>
            <Button type="submit" variant="destructive" disabled={!matches} loading={pending}>
              {t('routes.platform.detail.wipe.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
